import React, { useCallback, useMemo, useState, useEffect } from 'react';
import pt from 'prop-types';
import currency from 'currency.js';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { capitalize } from '@material-ui/core';
import styles from './ReviewModal.module.scss';
import Modal from './Modal';
import { SheetPropType } from './PropTypes';
import DisplayableSheet from './DisplayableSheet';
import penIcon from '../../../assets/pen.png';
import warningIcon from '../../../assets/warning.png';
import LoadingOverlay from './LoadingOverlay';
import cn from 'classnames';
import Builder from './Builder';
import discountedPrice, { grantsFreeAnnouncements } from '../../../utils/offerPricing';
import { sheetUnitPrice } from '../../../utils/functions';
import { isGrantedSheet } from '../../../utils/freeOfferAddon';

// Subtotal that reflects the active offer: one announcement bundle is free
// (grantsFreeAnnouncements), every other sheet takes the percentage. Mirrors
// the server's charge so this preview matches checkout.
const isAnnouncementSheet = (sheet) => /announcement/i.test(sheet?.addon?.name || '');
// sheetUnitPrice: server-stamped snapshot for persisted sheets, shared Rule-A
// fallback keyed on the order package (the old code always took priceWithout,
// overstating attached-to-a-paid-package gifts vs what checkout charges).
const sheetBasePrice = (sheet, orderPackage) => Number(sheetUnitPrice(sheet, orderPackage)) || 0;

// What the review would say with NO offer applied: every sheet at its base
// price. Shown struck through above the real subtotal when a granted free
// item is in the list, so the parent sees the value of the gift and the
// $0.00 it costs them (Katie, 2026-10-18).
export const reviewListSubtotal = (sheets, orderPackage = null) => (
  (sheets || []).reduce((total, sheet) => total + sheetBasePrice(sheet, orderPackage), 0)
);

export const hasGrantedSheet = (sheets) => (sheets || []).some(isGrantedSheet);

export const reviewSubtotal = (sheets, offer, qualifiesForFreeAnnouncements, orderPackage = null) => {
  // The free bundle is the MOST EXPENSIVE announcement -- matching the server's
  // Cart#free_announcement_bundle_price (.max), so the preview can't diverge from
  // the charge on a multi-bundle cart. Free bundle ONLY unlocks when the cart
  // actually qualifies (senior portraits package > $1) -- otherwise the offer
  // may resolve for the student but announcements-only carts still pay full
  // price, matching Coupon.calculate_discount on the server.
  let freeIndex = -1;
  if (grantsFreeAnnouncements(offer) && qualifiesForFreeAnnouncements) {
    sheets.forEach((sheet, i) => {
      if (isAnnouncementSheet(sheet)
        && (freeIndex === -1 || sheetBasePrice(sheet, orderPackage) > sheetBasePrice(sheets[freeIndex], orderPackage))) {
        freeIndex = i;
      }
    });
  }

  return sheets.reduce((total, sheet, i) => {
    if (i === freeIndex) return total; // the free bundle -> $0
    // A sheet an OFFER granted (#5747) is stamped at list price and its coupon
    // takes exactly that off at checkout -- so here it is $0, and the row says
    // FREE, or the review quoted "$29.95" for a gift (Katie, 2026-10-18).
    if (isGrantedSheet(sheet)) return total;
    const discounted = discountedPrice(sheetBasePrice(sheet, orderPackage), offer);
    return total + (discounted != null ? discounted : sheetBasePrice(sheet, orderPackage));
  }, 0);
};

export default function ReviewModal({ onCancel = () => {}, poses = [], backgrounds = [], cancelText = 'Cancel', additionalPoses, isLoading, errors, option, onConfirm, sheets, setIndex, offer = null, qualifiesForFreeAnnouncements = false, orderPackage = null }) {
  const [editableSheetIndex, setEditableSheetIndex] = useState(-1);
  const [editableSheets, setEditableSheets] = useState(sheets);

  const handleConfirm = useCallback(() => onConfirm(editableSheets, additionalPoses), [editableSheets, additionalPoses]);
  const handleCancel = useCallback(() => onCancel(editableSheets, additionalPoses, true), [editableSheets, additionalPoses]);

  const handleSheetEdit = (sheets) => {
    const newSheets = editableSheets.map((sheet, i) => {
      if (i !== editableSheetIndex) return sheet;

      return sheets[0];
    });

    setEditableSheets(newSheets);
    setEditableSheetIndex(-1);
    document.querySelectorAll('.ReactModalPortal').forEach((node) => node.style.display = 'block');
  };

  const startEdit = (i) => {
    document.querySelectorAll('.ReactModalPortal').forEach((node) => node.style.display = 'none');
    setEditableSheetIndex(i);
  };

  useEffect(() => {
    setEditableSheets(sheets);
  }, [sheets]);

  return (
    <>
      {sheets[editableSheetIndex] && (
        <div className={styles.singleSheetEditor}>
          <Builder
            option={option}
            backgrounds={backgrounds}
            favoriteIds={[]}
            imageTypes={[sheets[editableSheetIndex].imageType]}
            initialSheets={[sheets[editableSheetIndex]]}
            poses={poses}
            inReview
            onConfirm={handleSheetEdit}
          />
        </div>
      )}
      <Modal>
        <Modal.Trigger>Review</Modal.Trigger>
        <Modal.Content className={cn(styles.content, 'reviewModal')}>
          <div style={{ position: 'relative' }}>
            {isLoading && <LoadingOverlay />}
            <div>
              <div className={styles.headerWrapper}>
                <Modal.Title className={styles.title}>Review Your Package</Modal.Title>
                <Modal.Close className={styles.close}>X</Modal.Close>
              </div>
              {Object.keys(errors).length > 0 && (
                <div className={styles.errorSection}>
                  <img src={warningIcon} alt="Warning" />
                  <p>
                    There was a problem generating your package.
                    Please refresh the page and try again, come back at a later date or call
                    {' '}
                    <a className={styles.link} href="tel:209-567-0748">(209) 567-0748</a>
                    {' '}
                    for assistance.
                  </p>
                </div>
              )}
              {Object.keys(errors).length === 0 && (
                <div>
                  <p className={styles.optionName}>{option.name}</p>
                  <div>
                    {editableSheets.map((sheet, i) => (
                      <div key={i} className={styles.sheetsWrapper}>
                        <div className={styles.sheetsHeader}>
                          <div>
                            {capitalize(sheet?.imageType?.name?.toLowerCase())}
                            {isGrantedSheet(sheet) && (
                              <span
                                className="ml-2 inline-block rounded-full px-2 py-0.5 text-xs font-extrabold uppercase tracking-wide text-white"
                                style={{ backgroundColor: '#008fcf' }}
                              >
                                Free
                              </span>
                            )}
                          </div>
                          <button type="button" className={styles.editButton} onClick={() => startEdit(i)}>
                            <img alt="Edit" src={penIcon} />
                            <div>Edit</div>
                          </button>
                        </div>
                        <div className={styles.displayableSheets}>
                          <DisplayableSheet className={styles.displayableSheet} sheet={sheet} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.subtotal}>
                    {editableSheets.length > 0 && (
                      <span>
                        {'Subtotal: '}
                        {/* With a granted free item in the list: the list price
                            struck through, then what it actually costs. */}
                        {hasGrantedSheet(editableSheets) && (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#6b7280', marginRight: '0.4em' }}>
                              {currency(reviewListSubtotal(editableSheets, orderPackage)).format()}
                            </span>
                          </>
                        )}
                        {currency(reviewSubtotal(editableSheets, offer, qualifiesForFreeAnnouncements, orderPackage)).format()}
                      </span>
                    )}
                  </div>
                  <div className={styles.footer}>
                    <button onClick={handleConfirm} type="button">Continue to Checkout</button>
                    <Modal.Close onClick={handleCancel}>{cancelText}</Modal.Close>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
}

ReviewModal.defaultProps = {
  additionalPoses: 0,
  isLoading: false,
  errors: {},
};

ReviewModal.propTypes = {
  additionalPoses: pt.number,
  option: pt.shape({
    additionalPoseCharge: pt.number.isRequired,
    id: pt.string.isRequired,
    name: pt.string.isRequired,
    prices: pt.arrayOf(pt.shape({
      price: pt.number.isRequired,
    })),
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: pt.object,
  onConfirm: pt.func.isRequired,
  isLoading: pt.bool,
  sheets: pt.arrayOf(SheetPropType).isRequired,
  setIndex: pt.func.isRequired,
};
